/**
 * EntityChip - Shared component for rendering clickable entity links.
 *
 * Displays a chip with an icon and label that links to the entity's detail page.
 * Supports items, boards, workspaces, users, agents, and chats.
 * Icon color matches the target entity's module theme color.
 */
import React from 'react';
import { useAutoDirection } from './BidiText';
import {
  ClipboardList,
  LayoutGrid,
  Folder,
  File,
  User,
  Bot,
  MessageSquare,
  Building2,
  Contact,
  Handshake,
  Briefcase,
  UserSearch,
  FileText,
  ClipboardCheck,
  GitFork,
  Shapes,
  CircleDot,
  Package,
  PackageCheck,
  Ship,
  X,
  Pencil,
  Eye,
  Plus,
  Trash2,
} from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';
import type { ResolvedEntity } from '../../types';
import {
  getEntityByTable,
  getEntityByTableSuffix,
  getEntityIconComponent,
} from '../../config/modules.config';
import { getEntityUrl as getEntityUrlShared } from '../../utils/entityUrls';

/**
 * Semantic tints a chip may take instead of its module accent. Same vocabulary
 * as `StatusChip`'s tones, so the two read as one system. The `text-*` utilities
 * resolve to the AA-safe `-strong` foregrounds (see STYLE_GUIDE § Theme & Colors),
 * so each pair clears 4.5:1 without hand-picking a foreground.
 */
export type EntityChipTone = 'info' | 'success' | 'warning' | 'destructive';

/**
 * Static class strings (never `bg-${tone}/10`) so Tailwind's JIT emits them.
 */
const TONE_CLASS: Record<EntityChipTone, string> = {
  info: 'bg-info/10 text-info border-info/40',
  success: 'bg-success/10 text-success border-success/40',
  warning: 'bg-warning/10 text-warning border-warning/40',
  destructive: 'bg-destructive/10 text-destructive border-destructive/40',
};

export interface EntityChipProps {
  /** Resolved entity data from the API */
  entity: ResolvedEntity;
  /** Chip size */
  size?: 'sm' | 'default';
  /** Chip variant */
  variant?: 'outline' | 'default';
  /**
   * Semantic tint that REPLACES the target module's accent on this chip, for the
   * rare case where the reference itself carries meaning beyond "another record"
   * — e.g. a purchase order's vendor chip tinted `info` because the vendor is one
   * of the tenant's own companies (an intercompany document).
   *
   * Use it sparingly: the module accent is what makes a chip readable as "this
   * points at module X", and overriding it everywhere would erase that. Colour is
   * also never the ONLY carrier of the meaning — pair it with a `title` /
   * screen-reader label at the call site.
   */
  tone?: EntityChipTone;
  /**
   * Character budget for the label, applied before the CSS-level containment
   * below. Keeps a chip from claiming the whole row in a container that has no
   * width of its own (an unbounded flex row on a detail page). Ignored in
   * `overflow="wrap"` mode, where the full label is shown and wrapped.
   */
  maxLabelLength?: number;
  /**
   * Cap the chip tighter than its container. Accepts a CSS length (e.g.
   * `'12rem'`) or a number (interpreted as px). A chip is ALWAYS capped at
   * 100% of its container — pass this only to make it narrower still.
   */
  maxWidth?: number | string;
  /**
   * How a label too long for the chip's width is contained:
   * - `'truncate'` (default) — one line, ellipsized. Keeps table rows a single
   *   line high, so this is the right mode in a list-page cell.
   * - `'wrap'` — the full label wraps across lines, clamped to `maxLines`. For
   *   roomier surfaces (detail-page panels, drawers) where the whole name matters.
   */
  overflow?: 'truncate' | 'wrap';
  /**
   * In `overflow="wrap"` mode, the wrapped label is clamped to this many lines
   * and then ellipsized. Defaults to 2 rows. Ignored when truncating.
   */
  maxLines?: 1 | 2 | 3 | 4;
  /**
   * Extra placeholder values for the target entity's `urlContextTemplate`,
   * beyond the `{id}` the chip already carries and the `{board_id}` it reads off
   * `entity.board_id`.
   *
   * Some entities cannot be addressed by id alone — a checklist template's route
   * is `/people/{kind}/templates/{id}`, because one table serves both the
   * onboarding and the offboarding family. Those declare a `urlContextTemplate`
   * and NO `urlTemplate`, so a caller that cannot supply the context gets a
   * non-clickable chip rather than a link into the wrong family.
   */
  context?: Record<string, string>;
  /** For items: link to full page instead of board drawer (default: false) */
  linkToFullPage?: boolean;
  /** Callback when delete icon is clicked. If provided, shows delete icon. */
  onDelete?: (entity: ResolvedEntity) => void;
  /** Open link in a new browser tab */
  openInNewTab?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * Normalize entity type aliases to canonical table names.
 * Email integration stores some refs with legacy 'crm_' prefixes.
 */
function normalizeEntityType(entityType: string): string {
  switch (entityType) {
    case 'crm_contacts':
      return 'contacts';
    case 'crm_companies':
      return 'companies';
    default:
      return entityType;
  }
}

/** Get the appropriate icon for an entity type */
function getEntityIcon(entityType: string): React.ReactElement {
  const iconClass = 'h-3 w-3';
  switch (entityType) {
    case 'tasks':
      return <ClipboardList className={iconClass} />;
    case 'boards':
      return <LayoutGrid className={iconClass} />;
    case 'workspaces':
      return <Folder className={iconClass} />;
    case 'folders':
      return <Folder className={iconClass} />;
    case 'files':
      return <File className={iconClass} />;
    case 'users':
      return <User className={iconClass} />;
    case 'agents':
      return <Bot className={iconClass} />;
    case 'chats':
      return <MessageSquare className={iconClass} />;
    case 'contacts':
      return <Contact className={iconClass} />;
    case 'pipeline_deals':
      return <Handshake className={iconClass} />;
    case 'companies':
      return <Building2 className={iconClass} />;
    case 'people_onboarding_templates':
      return <ClipboardCheck className={iconClass} />;
    case 'recruiting_job_postings':
      return <Briefcase className={iconClass} />;
    case 'recruiting_candidates':
      return <UserSearch className={iconClass} />;
    case 'fbpm_forms':
      return <FileText className={iconClass} />;
    case 'fbpm_scorecards':
      return <ClipboardCheck className={iconClass} />;
    case 'fbpm_business_processes':
      return <GitFork className={iconClass} />;
    case 'fbpm_process_instances':
      return <GitFork className={iconClass} />;
    case 'ctr_contracts':
      return <FileText className={iconClass} />;
    case 'proc_purchase_orders':
      return <FileText className={iconClass} />;
    case 'proc_goods_receipts':
      return <PackageCheck className={iconClass} />;
    case 'shp_shipments':
      return <Ship className={iconClass} />;
    case 'accounting_bills':
      return <FileText className={iconClass} />;
    case 'contacts_organizations':
      return <Building2 className={iconClass} />;
    case 'tenants':
      return <Building2 className={iconClass} />;
    case 'product_categories': {
      // Resolved from the entity registry rather than spelled here: Master
      // Data's own category pages, the sales inquiry review grid and the
      // procurement markup settings must all draw ONE glyph, and four had
      // drifted apart (`Tag`/`Boxes`/`FolderTree`/`Shapes`, WI bbb2fcf4).
      const CategoryIcon = getEntityIconComponent('product_categories') ?? Shapes;
      return <CategoryIcon className={iconClass} />;
    }
    case 'products':
      return <Package className={iconClass} />;
    case 'sf_work_items': {
      // Registry-resolved for the same reason as `product_categories` above: the
      // work item's glyph is declared once in `modules_entities.config.json`
      // (`CircleDot`), and spelling it here as well is how two surfaces end up
      // drawing different icons for one entity. Without a case at all it lands on
      // the `ClipboardList` default — the TASK icon — so a work-item chip and a
      // task chip would be indistinguishable.
      const WorkItemIcon = getEntityIconComponent('sf_work_items') ?? CircleDot;
      return <WorkItemIcon className={iconClass} />;
    }
    default:
      return <ClipboardList className={iconClass} />;
  }
}

/** Get the module color for an entity type (table name) */
function getModuleColorForEntity(entityType: string): string | undefined {
  const entityInfo = getEntityByTable(entityType) ?? getEntityByTableSuffix(entityType);
  return entityInfo?.module.color;
}

/** Get the URL for an entity based on its type and metadata */
function getEntityUrl(
  entity: ResolvedEntity,
  linkToFullPage = false,
  context?: Record<string, string>,
): string | null {
  return getEntityUrlShared({
    entityType: entity.entity_type,
    entityId: entity.entity_id,
    boardId: entity.board_id,
    context,
    linkToFullPage,
  });
}

/** Get an action indicator icon for CRUD operations */
function getActionIcon(action?: 'create' | 'read' | 'update' | 'delete'): React.ReactElement | null {
  if (!action) return null;
  const cls = 'h-3 w-3 flex-shrink-0';
  switch (action) {
    case 'create':
      return <Plus className={cn(cls, 'text-success')} />;
    case 'read':
      return <Eye className={cn(cls, 'text-muted-foreground')} />;
    case 'update':
      return <Pencil className={cn(cls, 'text-warning')} />;
    case 'delete':
      return <Trash2 className={cn(cls, 'text-destructive')} />;
    default:
      return null;
  }
}

/**
 * Map a wrapped chip's line cap to its static Tailwind `line-clamp-*` utility.
 * Static entries (not `line-clamp-${n}`) so Tailwind's JIT emits them. Each
 * clamps the label to N rows and appends an ellipsis.
 */
const LINE_CLAMP_CLASS: Record<1 | 2 | 3 | 4, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
};

/** Truncate text with ellipsis if it exceeds maxLength */
function truncateLabel(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 1)}...`;
}

/**
 * Renders a chip that links to an entity's detail page.
 * Icon color uses the target module's theme color.
 *
 * @example
 * ```tsx
 * <EntityChip
 *   entity={{ entity_type: 'items', entity_id: 'abc', title: 'Fix login bug', board_id: 'xyz' }}
 *   size="sm"
 * />
 * ```
 */
export function EntityChip({
  entity,
  size = 'sm',
  variant = 'outline',
  tone,
  maxLabelLength = 30,
  maxWidth,
  overflow = 'truncate',
  maxLines = 2,
  context,
  linkToFullPage = false,
  onDelete,
  openInNewTab,
  className,
}: EntityChipProps) {
  const normalizedType = normalizeEntityType(entity.entity_type);
  const normalizedEntity = normalizedType !== entity.entity_type
    ? { ...entity, entity_type: normalizedType }
    : entity;
  const url = getEntityUrl(normalizedEntity, linkToFullPage, context);
  const label = entity.title || entity.entity_ref;
  // Wrap mode shows the whole label across lines; truncate mode keeps the chip
  // one line high and spends its character budget first.
  const wrap = overflow === 'wrap';
  const truncatedLabel = wrap ? label : truncateLabel(label, maxLabelLength);
  // An entity title is user data — resolve its direction from the FULL label, so
  // truncation cannot change which way the chip reads.
  const labelDir = useAutoDirection(label);
  const icon = getEntityIcon(normalizedType);
  // A tinted chip drops the module accent entirely — border, icon and label all
  // come from the semantic tone, or the two colour systems would fight (the
  // accent is an inline style and would win the border back from the class).
  const moduleColor = tone ? undefined : getModuleColorForEntity(normalizedType);
  const maxWidthValue = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

  const handleDelete = onDelete
    ? (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onDelete(entity);
      }
    : undefined;

  const actionIcon = getActionIcon(entity.action);

  const content = (
    <>
      <span className={cn('inline-flex shrink-0 items-center gap-0.5', wrap && 'mt-0.5')}>
        <span style={{ color: moduleColor }}>{icon}</span>
        {actionIcon}
      </span>
      <span
        dir={labelDir}
        className={cn(
          'min-w-0',
          wrap
            ? cn('whitespace-normal break-words', LINE_CLAMP_CLASS[maxLines])
            : 'truncate'
        )}
      >
        {truncatedLabel}
      </span>
      {onDelete && (
        <button
          onClick={handleDelete}
          className="ms-1 shrink-0 rounded-full p-0.5 hover:bg-muted"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </>
  );

  const chipClassName = cn(
    // A chip is always contained: it can never grow past its container, and it
    // may shrink below its content width so the label's truncation kicks in.
    'inline-flex items-center gap-1.5 font-normal max-w-full min-w-0',
    size === 'sm' && 'text-xs px-2 py-0.5',
    // Wrapping chips run their label across lines: top-align the icon, allow
    // multi-line height, and square the corners (a full pill looks odd on
    // multiple lines).
    wrap && 'items-start whitespace-normal rounded-md',
    url && 'cursor-pointer hover:bg-muted/80',
    tone && TONE_CLASS[tone],
    className
  );

  const style: React.CSSProperties | undefined =
    moduleColor || maxWidthValue
      ? { ...(moduleColor ? { borderColor: moduleColor } : {}), ...(maxWidthValue ? { maxWidth: maxWidthValue } : {}) }
      : undefined;

  // The label can be shortened by its character budget OR clipped by the chip's
  // width, and the second is invisible to JS — so always carry the full name in
  // a title tooltip.
  const title = label;

  // If we can't generate a URL, render a non-clickable chip
  if (!url) {
    return (
      <Badge variant={variant} className={chipClassName} style={style} title={title}>
        {content}
      </Badge>
    );
  }

  return (
    // The anchor has to be contained too, or the badge's `max-w-full` resolves
    // against an inline box that has already outgrown the cell.
    // eslint-disable-next-line i18next/no-literal-string -- HTML anchor attribute tokens, not user copy
    <RouterLink to={url} className="no-underline inline-flex min-w-0 max-w-full align-middle" {...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      <Badge variant={variant} className={chipClassName} style={style} title={title}>
        {content}
      </Badge>
    </RouterLink>
  );
}
